import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Badge,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  alpha,
  useTheme,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Notifications,
  Refresh,
  CheckCircle,
  AccessTime,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getPhaseColor, getRiskColor, getRiskLabel } from './utils';
import { format, parseISO } from 'date-fns';

const AlertsSection = ({
  alerts,
  alertsLoading,
  alertCount,
  onRefresh
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 3,
        background: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.grey[50], 0.3)} 100%)`,
          overflow: 'visible',
        }}
      >
        {/* Header */}
        <CardContent sx={{ p: 3, pb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                }}
              >
                <Notifications sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.5 }}>
                    {t('downtrend.alerts.title')}
                  </Typography>
                  <Badge
                    badgeContent={alertCount}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.75rem',
                        height: 22,
                        minWidth: 22,
                        fontWeight: 700,
                      },
                    }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('downtrend.alerts.activeDowntrendAlerts', 'Active downtrend alerts')}
                </Typography>
              </Box>
            </Stack>
            <Tooltip title={t('downtrend.alerts.refresh', 'Làm mới')}>
              <Button
                onClick={onRefresh}
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                disabled={alertsLoading}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  borderWidth: 1.5,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderWidth: 1.5,
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                  '&:disabled': {
                    borderColor: alpha(theme.palette.action.disabled, 0.3),
                    color: alpha(theme.palette.action.disabled, 0.5),
                    bgcolor: alpha(theme.palette.action.disabled, 0.04),
                  },
                  '& .MuiButton-startIcon': {
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover .MuiButton-startIcon': {
                    transform: 'rotate(180deg)',
                  },
                }}
              >
                {t('downtrend.alerts.refresh', 'Làm mới')}
              </Button>
            </Tooltip>
          </Stack>
        </CardContent>

        {/* Content */}
        <CardContent sx={{ pt: 0 }}>
          {alertsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
              <Stack alignItems="center" spacing={2}>
                <CircularProgress size={48} />
                <Typography variant="body2" color="text.secondary">
                  {t('downtrend.alerts.loading', 'Loading alerts...')}
                </Typography>
              </Stack>
            </Box>
          ) : alerts.length > 0 ? (
            <Stack spacing={2.5}>
              {alerts.map((alert, idx) => {
                const phaseInfo = getPhaseColor(alert.phase);
                const riskColor = getRiskColor(alert.risk_score || 0);
                const riskValue = alert.risk_score || 0;

                return (
                  <Card
                    key={idx}
                    elevation={0}
                    sx={{
                      border: `1px solid ${alpha(phaseInfo.color, 0.3)}`,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${alpha(phaseInfo.color, 0.05)} 0%, ${alpha(phaseInfo.color, 0.02)} 100%)`,
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 16px ${alpha(phaseInfo.color, 0.2)}`,
                        borderColor: alpha(phaseInfo.color, 0.5),
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: 4,
                        height: '100%',
                        background: `linear-gradient(135deg, ${phaseInfo.color} 0%, ${phaseInfo.color} 100%)`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, pl: 4 }}>
                      <Stack spacing={2.5}>
                        {/* Header Row */}
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          flexWrap="wrap"
                          gap={1.5}
                        >
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                background: `linear-gradient(135deg, ${phaseInfo.color} 0%, ${phaseInfo.color} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 2px 8px ${alpha(phaseInfo.color, 0.3)}`,
                              }}
                            >
                              {React.cloneElement(phaseInfo.icon, {
                                sx: { color: 'white', fontSize: 20 },
                              })}
                            </Box>
                            <Box>
                              <Typography variant="h6" fontWeight={700} sx={{ color: phaseInfo.color }}>
                                {alert.symbol}
                              </Typography>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                                <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(alert.alert_date)}
                                </Typography>
                              </Stack>
                            </Box>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <Chip
                              icon={React.cloneElement(phaseInfo.icon, { sx: { fontSize: 14 } })}
                              label={t(phaseInfo.labelKey)}
                              size="small"
                              sx={{
                                bgcolor: alpha(phaseInfo.color, 0.15),
                                color: phaseInfo.color,
                                fontWeight: 700,
                                border: `1px solid ${alpha(phaseInfo.color, 0.3)}`,
                                '& .MuiChip-icon': {
                                  color: phaseInfo.color,
                                },
                              }}
                            />
                            <Chip
                              label={`${riskValue.toFixed(1)}%`}
                              size="small"
                              sx={{
                                bgcolor: alpha(riskColor, 0.15),
                                color: riskColor,
                                fontWeight: 700,
                                border: `1px solid ${alpha(riskColor, 0.3)}`,
                                minWidth: 70,
                              }}
                            />
                          </Stack>
                        </Stack>

                        {/* Risk Score Progress */}
                        <Box>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {t('downtrend.alerts.overallRiskScore', 'Overall Risk Score')}
                            </Typography>
                            <Typography variant="caption" fontWeight={700} sx={{ color: riskColor }}>
                              {getRiskLabel(riskValue, t)}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={riskValue}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: alpha(riskColor, 0.1),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: riskColor,
                                borderRadius: 4,
                              },
                            }}
                          />
                        </Box>

                        {/* Risk Breakdown */}
                        {(alert.down_risk_10 || alert.down_risk_20 || alert.down_risk_60) && (
                          <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                              pt: 2,
                              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            }}
                          >
                            {alert.down_risk_10 && (
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {t('downtrend.alerts.10days', '10 Days')}
                                </Typography>
                                <Typography variant="body2" fontWeight={700} sx={{ color: getRiskColor(alert.down_risk_10 * 100) }}>
                                  {(alert.down_risk_10 * 100).toFixed(1)}%
                                </Typography>
                              </Box>
                            )}
                            {alert.down_risk_20 && (
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {t('downtrend.alerts.20days', '20 Days')}
                                </Typography>
                                <Typography variant="body2" fontWeight={700} sx={{ color: getRiskColor(alert.down_risk_20 * 100) }}>
                                  {(alert.down_risk_20 * 100).toFixed(1)}%
                                </Typography>
                              </Box>
                            )}
                            {alert.down_risk_60 && (
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {t('downtrend.alerts.60days', '60 Days')}
                                </Typography>
                                <Typography variant="body2" fontWeight={700} sx={{ color: getRiskColor(alert.down_risk_60 * 100) }}>
                                  {(alert.down_risk_60 * 100).toFixed(1)}%
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        )}

                        {/* Message */}
                        {alert.message && (
                          <Box
                            sx={{
                              pt: 2,
                              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                              {alert.message}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                }}
              >
                <CheckCircle sx={{ fontSize: 48, color: 'white' }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                {t('downtrend.alerts.noAlerts')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                {t('downtrend.alerts.noAlertsMessage')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
  );
};

export default AlertsSection;

